/**
 * @fileoverview GradeRateTimeline — one grade's dated rates, shown as periods
 * (MOR-1018).
 *
 * The rows are stored as start dates only. Shown that way, working out which
 * rate applied on a given day is left to the reader, which is exactly what
 * WLPR asked not to have to do. Here each rate gets its end date and a badge:
 * Current, Upcoming (future-dated, not charging yet) or nothing for the past.
 */

import { Trash2 } from "lucide-react";

import type { Id } from "@cvx/_generated/dataModel";

import { Badge, Button } from "@/ui/primitives";
import { formatIsoDate, formatRate, rateTimeline } from "./rateCards.helpers";

export function GradeRateTimeline({
  rates,
  today,
  canEdit,
  onDelete,
}: {
  rates: {
    _id: Id<"staffGradeRates">;
    hourlyRate: number;
    effectiveFrom: string;
  }[];
  today: string;
  canEdit: boolean;
  onDelete: (rateId: Id<"staffGradeRates">) => void;
}) {
  if (rates.length === 0) {
    return (
      <p className="text-xs text-muted-foreground">
        No rates yet. Add the first rate below.
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {rateTimeline(rates, today).map(({ rate, from, to, state }) => (
        <li key={rate._id} className="flex items-center gap-3 text-sm">
          <span className="w-28 tabular-nums">
            {formatRate(rate.hourlyRate)}
          </span>
          <span className="w-56 text-xs text-muted-foreground">
            {formatIsoDate(from)} {to ? `to ${formatIsoDate(to)}` : "onwards"}
          </span>
          {state === "current" && (
            <Badge color="emerald" variant="soft">
              Current
            </Badge>
          )}
          {state === "upcoming" && (
            <Badge color="blue" variant="soft">
              Upcoming
            </Badge>
          )}
          {canEdit && rates.length > 1 && (
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto size-6 text-muted-foreground hover:text-destructive"
              onClick={() => onDelete(rate._id)}
              aria-label={`Delete the ${formatRate(rate.hourlyRate)} rate from ${formatIsoDate(from)}`}
            >
              <Trash2 className="size-3" />
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
